// Copyright (c) 2019-24 Stax·ai, Inc.
// See LICENSE.md file in project root directory

import React from 'react';
import Loading from 'react-loading';
import { withRouter } from 'react-router-dom';
import Scrollbars from 'react-custom-scrollbars';
import ViewControl from '../ViewControl';

import api from '../../api';
import ContextMenu from '../ContextMenu';
import formatDate from '../../utilities/formatDate';
import getSourceIcon from '../../utilities/getSourceIcon';

import '../../styles/search.css';

class SearchResults extends React.Component {

    state = {
        results: [],
        count: 0,
        offset: 0,
        sort: "relevant",
        filter: {}, 
        view: "list",
        searching: false,
        override: false,
        expand: {},

        // Selection
        selection: [] // List of documents Ids
    }

    jumpCaptured = false
    prevQuery = ""

    componentDidMount = () => {
        if (this.props.archive)
            this.search(this.props.archive, false, true);
        else
            this.searchParams(this.props);
    }

    componentWillReceiveProps = (next) => {
        if (next.query !== this.props.query || (next.stack && (!this.props.stack || next.stack._id !== this.props.stack._id)))
            this.searchParams(next);

        else if (next.archive && next.archive !== this.props.archive)
            this.search(next.archive, false, true);
    }

    componentWillUnmount = () => {
        if (this.searchTimeout)
            clearTimeout(this.searchTimeout);
    }

    toggleSelection = async (docId) => {
        let selection = this.state.selection;
        const selIdx = selection.findIndex(s => s === docId);
        if (selIdx >= 0)
            selection.splice(selIdx, 1);

        else selection.push(docId);
        return this.setState({ selection });
    }

    highlight = (text, term) => {
        let chunks = [];
        let position = 0;
        while (true) {
            const idx = text.toLowerCase().indexOf(term.toLowerCase(), position);
            if (idx < 0) {
                chunks.push(text.substring(position));
                break;
            };
            
            chunks.push(text.substring(position, idx));
            // chunks.push("<>" + text.substring(idx, idx+term.length) + "</>");
            chunks.push(<span className="search-result-highlight">{text.substring(idx, idx+term.length)}</span>);
            position += idx + term.length;
        }
        return chunks.map((c, idx) => <React.Fragment key={idx}>{c}</React.Fragment>);
    }

    searchParams = async (props) => {
        const params = new URLSearchParams(props.location.search);
        const sort = params.get('sort');
        if (sort)
            await this.setState({ sort });

        const view = params.get('view');
        if (view)
            await this.setState({ view });

        const override = params.get('override');
        if (override === 'true')
            await this.setState({ override: true });

        const jumpTo = parseInt(params.get('result') ?? 0, 10);

        const format = params.get('format');

        this.search(props.query, format, false, false, jumpTo);
    }

    search = async (query, format=undefined, force = false, archive = false, jumpTo = 0) => {
        if ((archive && query.query.length < 3) || (!archive && query.length < 3)) return;
        // Clear results for a new search
        await this.setState(force || (archive ? query.query !== this.prevQuery : query !== this.prevQuery) ? {
            offset: 0,
            results: [],
            selection: [],
            searching: true
        } : {
            searching: true
        });

        this.prevQuery = archive ? query.query : query;

        if (this.searchTimeout)
            clearTimeout(this.searchTimeout); // Block previous search

        this.searchTimeout = setTimeout(async () => {

            let res;

            // Deep (archive) search filters
            if (archive)
                res = await api.post('/document/searchArchive', {
                    searchQuery: encodeURIComponent(query.query.trim()),
                    stacks: query.stack ? query.stack : undefined,
                    sources: query.source ? query.source : undefined,
                    receivedAfter: query.receivedFrom ? query.receivedFrom : undefined,
                    receivedBefore: query.receivedTo ? query.receivedTo : undefined
                });

            // Quick search
            else {
                let limit = 12, offset = this.state.offset;
                if (!this.jumpCaptured && jumpTo >= 12) {
                    limit = Math.ceil((1+jumpTo)/12) * 12;
                    offset = 0;

                    // Set offset in state
                    this.setState({ offset: limit - 12 });
                }

                let req = '/document/search?searchQuery=' + encodeURIComponent(query.trim()) 
                + '&searchLimit=' + limit 
                + '&searchOffset=' + offset 
                + '&sortBy=' + this.state.sort 
                + (format ? "&format=" + format : ""); 

                if (this.props.stack && this.props.stack._id && window.location.href.includes("/stack/") && !this.state.override)
                    req += '&stackId=' + this.props.stack._id;

                if (window.location.href.includes("/field/") && this.props.field && this.props.value)
                    req += '&field=' + this.props.field + '&value=' + this.props.value;

                res = await api.get(req);
            }

            await this.setState({
                searching: false,
                results: this.state.offset === 0 ? [] : this.state.results
            });

            if (!res.result || !res.count) return;

            await this.setState({
                count: res.count
            });

            const compileResult = (document) => ({
                stack: this.props.stacks ? this.props.stacks.find(s => s._id === document?.stack) : { _id: null, path: "Unclassified" },
                document,
                matches: document?.highlight,
            });

            if (!this.jumpCaptured && jumpTo) {
                this.jumpCaptured = true;

                // Load all results and jump to it immediately
                await this.setState({
                    results: res.result.map(document => compileResult(document)),
                    highlight: null
                });
                setTimeout(() => {
                    const result = document.getElementById("search-result-" + jumpTo);
                    if (!result) return;
                    const rect = result.getBoundingClientRect();
                    if (!rect) return;
                    this.refs['search-results-scrollbar'].view.scroll({
                        top: this.refs['search-results-scrollbar'].getScrollTop() + rect.top - 120,
                        behavior: 'smooth'
                    });

                    // this.refs['search-result-' + jumpTo].scrollIntoView({ behavior: 'smooth' });
                    this.setState({ highlight: jumpTo });
                }, 300);
            }

            else {
                for (let i=0; i<5; i++) {
                    const document = res.result[i];
                    if (!document) return; // Done

                    setTimeout(async () => {
                        await this.setState({
                            results: [
                                ...this.state.results,
                                compileResult(document)
                            ]
                        });
                    }, Math.log(0.4 * i + 1) * 400);
                }

                // Remaining can render together...
                if (res.result.length > 5) {
                    setTimeout(async () => {
                        await this.setState({
                            results: [
                                ...this.state.results,
                                ...res.result.slice(5).map(document => compileResult(document))
                            ]
                        })
                    }, 450);
                }
            }

        }, 500);

    }

    getStackName = (path) => {
        const cmp = path.split('/');
        return cmp[cmp.length - 1];
    }

    bulkUpdate = (diff) => {
        this.props.updateDocuments(this.state.selection, diff);
        this.props.search("");
    }

    bulkOperation = (op) => {
        op(this.state.selection);
        this.props.search("");
    }

    docOperation = (op, args) => {
        op(args);
        this.props.search("");
    }

    render = () => {
        return (<div
            className="search-results-container"
        >

            <img
                className="document-close"
                src="https://static.stax.ai/images/icons/close.png"
                alt="x"
                title="Close [Esc]"
                onClick={() => {
                    this.props.search("");
                    this.props.clearArchiveSearch();
                }}
            />

            {this.state.results.length > 0 ? <>
                <div className="search-count">
                    {this.state.count} results
                </div>
                <div
                    className="search-new-tab"
                    onClick={() => window.open(window.location.href + "/search/" + this.props.query?.trim(), '_blank')}
                >Open results in new tab ↗</div>
            </> : ""}

            <div className="search-results">
                <Scrollbars ref="search-results-scrollbar">

                    <div className="search-results-options">
                        {this.props.stack && window.location.href.includes("/stack/") && this.props.stack._id && <>
                            <div
                                className={"search-results-option" + (this.state.override ? "" : " search-results-option-active")}
                                onClick={async () => {
                                    await this.setState({ override: false });
                                    this.search(this.props.query?.trim(), true);
                                }}
                            // style={{ marginLeft: 30 }}
                            >Search in <u>{this.getStackName(this.props.stack.path)}</u></div>
                            <div
                                className={"search-results-option" + (this.state.override ? " search-results-option-active" : "")}
                                onClick={async () => {
                                    await this.setState({ override: true});
                                    this.search(this.props.query?.trim());
                                }}
                            >Search all stacks</div>
                        </>}

                        <div className="search-results-view">
                            <ViewControl
                                view={this.state.view}
                                setView={view => this.setState({ view })}
                                style={{
                                    width: 30,
                                    height: 30,
                                    padding: 0
                                }}
                            />
                        </div>

                        <div
                            className={"search-results-sort" + (this.state.sort === "recent" ? "" : " search-results-sort-active")}
                            onClick={async () => {
                                await this.setState({ sort: "relevant" });
                                this.search(this.props.query);
                            }}
                        >Relevant</div>

                        <div
                            className={"search-results-sort" + (this.state.sort !== "recent" ? "" : " search-results-sort-active")}
                            onClick={async () => {
                                await this.setState({ sort: "recent" });
                                this.search(this.props.query);
                            }}
                        >Recent</div>
                    </div>

                    <div className="search-result-items">
                        {document.title=this.props.query}
                        {this.state.results.filter(r => r.document).map((result, idx) => <div
                            key={"search-result-" + idx}
                            id={"search-result-" + idx}
                            ref={"search-result-" + idx}
                            className={"search-result-item" +
                                (this.state.view === "grid" ? " search-result-item-grid" : "") +
                                (this.state.selection.includes(result?.document?._id) ? " search-result-item-selected" : "") +
                                (this.state.highlight === idx ? " search-result-item-highlight" : "")
                            }
                            title="Click to open. Ctrl+Click to Select."
                            onClick={async (e) => {
                                // If user is holding down control, then select/deselect document
                                if (e.ctrlKey)
                                    return this.toggleSelection(result.document._id);

                                await this.props.search("");
                                let target = '/stack/';

                                // Document stack
                                target += result.document.stack ? result.document.stack : 'unclassified';

                                // Document ID
                                target += '/document/' + result.document._id;

                                // Search query
                                target += '/search/*' + this.props.query?.trim().replace(/\//g, '|') + '?';

                                // Override stack search?
                                if (this.state.override)
                                    target += 'override=true&';

                                // From stack view?
                                if (window.location.href.includes('/stack/') && this.props.stack && this.props.stack._id)
                                    target += 'from=' + this.props.stack._id + '&';

                                // From field view?
                                else if (window.location.href.includes('/field/') && this.props.field && this.props.value)
                                    target += 'from=field&field=' + this.props.field + '&value=' + this.props.value + '&';

                                // From root...
                                else target += 'from=root&'

                                // Sort, view and result index
                                target += 'sort=' + this.state.sort + '&view=' + this.state.view + '&result=' + idx;

                                this.props.history.push(target);
                            }} 
                            onContextMenu={e => {
                                this.openMenu(e, result.document);
                            }}
                            onDragStart={() => {
                                if (this.state.selection.length > 0 && this.state.selection.includes(result.document._id))
                                    this.props.moveDocuments(this.state.selection);

                                else {
                                    this.setState({ selection: [] });
                                    this.props.moveDocuments([result.document._id]);
                                }

                                this.props.search("");
                            }}
                        >

                            {result.document.thumbnail ? <img
                                className="search-result-thumbnail"
                                alt={result.document.name}
                                src={result.document.thumbnail}
                            /> : result.document.format ? <img
                                className="search-result-thumbnail-format"
                                alt={result.document.name}
                                src={"https://static.stax.ai/images/format/" + result.document.format + ".png"}
                            /> : <div className="search-result-nothumb" />}

                            <img
                                className="search-result-source"
                                src={getSourceIcon(result.document.source)}
                                alt={result.document.source}
                            />

                            <h1
                                title={result.document.name + " (" + String(result.document.pages) + " pages)"}
                            >{result.document.name} ({result.document.pages} pages)</h1>
                            <h2>{result.stack ? result.stack.path : "Unclassified"}</h2>
                            <h3>{result.document.source}</h3>

                            <div className="search-result-meta">
                                Received: {formatDate(result.document.receivedOn)}<br />
                                Modified: {formatDate(result.document.lastModified)}
                            </div>

                            {/* Matches in metadata */}
                            {result?.document?.metadata?.length ? <div className="search-result-metadata">
                                {result.document.metadata.slice(0,3).map((field, jdx) => <div
                                    className="search-result-metadata-item"
                                    key={jdx}
                                >
                                    <div className="search-result-metadata-key">{field.key}</div>
                                    <div className="search-result-metadata-value">{this.highlight(field.value, this.props.query)}</div>
                                </div>)}
                            </div> : ""}

                            {/* Matches in content */}
                            {result && result.matches && result.matches !== null ? <div className="search-result-matches">
                                {(this.state.expand[result.document._id] ? result.matches : result.matches.slice(0,3)).map((match,jdx) => <div 
                                    className="search-result-match-item"
                                    key={jdx}
                                >...
                                    {(match.split("<HK8IjzqIGIQWPMUMJUx8uzY0jAJJRTGV>")).map(
                                        (splitres, jdx) => {
                                            let splitarr = splitres.split("<7iyPPAwjJhd9942Fkcr2eziSGJoevYac>")
                                            if (splitarr.length > 1) {
                                                return (<React.Fragment key={jdx}><span className="search-result-highlight">
                                                    {splitarr[0]}
                                                </span><span>{splitarr[1]}</span></React.Fragment>)
                                            }
                                            return (<span key={jdx}>
                                                {splitarr[0]}
                                            </span>)
                                        }
                                    )}...
                                </div>)}
                                {!this.state.expand[result.document._id] && result.matches.length > 3 && <div
                                    className="search-result-more"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        this.setState({
                                            expand: {
                                                ...this.state.expand,
                                                [result.document._id]: true
                                            }
                                        });
                                    }}
                                >Load more matches...</div>}
                            </div> : ""}

                        </div>)}
                    </div>

                    <div style={{ clear: 'both', height: 5 }} />

                    {this.state.searching ? <div className="search-loading"><Loading
                        type="bubbles"
                        color="var(--color-primary)"
                        height={100}
                        width={100}
                    /></div> :
                        this.state.count > this.state.results.length ? <div
                            className="search-load-more"
                            onClick={async () => {
                                await this.setState({
                                    offset: this.state.offset + 12
                                });
                                this.search(this.props.query);
                            }}
                        >Load more results...</div> :
                            this.state.results.length === 0 ? <div className="search-no-results">
                                Your search did not return any results...
                    </div> : ""}

                </Scrollbars>

                <ContextMenu
                    load={(open, close) => {
                        this.openMenu = open;
                        this.closeMenu = close;
                    }}
                    options={(doc) => {
                        const options = [];

                        if (!doc) return options;

                        // Context-menu options for selected document
                        if (this.state.selection.includes(doc._id)) {
                            options.push({
                                label: "Unselect",
                                action: (doc) => this.toggleSelection(doc._id)
                            },
                                {
                                    label: "Mark all as read",
                                    action: () => this.bulkUpdate({ reviewed: true })
                                },
                                {
                                    label: "Mark all unread",
                                    action: () => this.bulkUpdate({ reviewed: false })
                                },
                                {
                                    label: "Mark all as urgent",
                                    action: () => this.bulkUpdate({ urgent: true })
                                },
                                {
                                    label: "Mark all not urgent",
                                    action: () => this.bulkUpdate({ urgent: false })
                                },
                                {
                                    label: "Move all",
                                    action: () => this.bulkOperation(this.props.moveDocuments)
                                },
                                {
                                    label: "Re-sort all",
                                    action: () => this.bulkOperation(this.props.resortDocuments)
                                },
                                {
                                    label: "Delete all",
                                    action: () => this.bulkOperation(this.props.removeDocuments)
                                });
                        }

                        else
                            options.push({
                                label: "Select",
                                action: (doc) => this.toggleSelection(doc._id)
                            },
                                {
                                    label: "Open document",
                                    action: (doc) => this.props.history.push('/stack/' + doc.stack + '/document/' + doc._id)
                                },
                                {
                                    label: "Open in new tab",
                                    action: (doc) => window.open('/stack/' + doc.stack + '/document/' + doc._id, '_blank')
                                },
                                {
                                    label: "Download",
                                    action: (doc) => this.props.downloadDocument(doc)
                                },
                                {
                                    label: "Print",
                                    action: (doc) => this.props.printDocument(doc)
                                },
                                {
                                    label: "Share",
                                    action: (doc) => this.props.sendDocument(doc)
                                },
                                {
                                    label: "Move",
                                    action: (doc) => this.docOperation(this.props.moveDocuments, [doc._id])
                                },
                                {
                                    label: "Re-sort",
                                    action: (doc) => this.docOperation(this.props.resortDocuments, [doc._id])
                                },
                                {
                                    label: "Delete",
                                    action: (doc) => this.docOperation(this.props.removeDocuments, [doc._id])
                                });

                        if (this.state.selection.length < this.state.results.length)
                            options.push({
                                label: "Select all",
                                action: () => this.setState({ selection: this.state.results.map(result => result.document._id) })
                            });

                        if (this.state.selection.length === 0)
                            options.push({
                                label: "Unselect all",
                                action: () => this.setState({ selection: [] })
                            });

                        return options;
                    }}
                />
            </div>

        </div>);
    }

}

export default withRouter(SearchResults);